import { useEffect } from "react";
import { useAppDispatch, useAppSelector, useCan } from "@/state/hooks";
import { CAP } from "@/api/capabilities";
import {
  applyTradingAccount,
  applyTradingOrders,
  applyTradingPositions,
  applyTradingSnapshot,
  setTradingStreamConnected,
} from "@/state/slices/tradingSlice";
import type {
  TradingAccountResponse,
  TradingOrdersResponse,
  TradingPositionsResponse,
} from "@/api/types";
import { apiUrl } from "@/api/client";

/** Subscribe to `/api/trading/stream` (Server-Sent Events) for live
 *  account / positions / orders. One process-wide pump on the
 *  backend polls the trading server and pushes only changed resources, so wire
 *  traffic stays near-zero when nothing's happening.
 *
 *  Replaces the previous per-client polling loop, which fired
 *  three REST calls every 4s regardless of whether anything had
 *  changed.
 *
 *  Connects only for owner sessions (the endpoint 403s guests,
 *  which would otherwise put the EventSource into an infinite
 *  reconnect loop). EventSource handles auto-reconnect natively
 *  on network blips so we don't need a manual retry loop here. */
export function useTradingStream(): void {
  const dispatch = useAppDispatch();
  const isOwner = useCan(CAP.tradingRead);
  const meStatus = useAppSelector((s) => s.me.status);

  useEffect(() => {
    if (meStatus !== "succeeded" || !isOwner) return;

    const source = new EventSource(apiUrl("/api/trading/stream"), {
      withCredentials: true,
    });

    source.addEventListener("open", () => {
      dispatch(setTradingStreamConnected(true));
    });

    // The pump's bootstrap message — fired exactly once per
    // connection, immediately after subscribe. Carries whatever
    // the latest poll cycle observed; `warming: true` means the
    // first cycle hasn't completed yet (server just started).
    source.addEventListener("snapshot", (e: MessageEvent) => {
      try {
        const payload = JSON.parse(e.data);
        dispatch(applyTradingSnapshot(payload));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("the trading server stream: failed to parse snapshot", err);
      }
    });

    source.addEventListener("account", (e: MessageEvent) => {
      try {
        const payload: TradingAccountResponse = JSON.parse(e.data);
        dispatch(applyTradingAccount(payload));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("the trading server stream: failed to parse account event", err);
      }
    });

    source.addEventListener("positions", (e: MessageEvent) => {
      try {
        const payload: TradingPositionsResponse = JSON.parse(e.data);
        dispatch(applyTradingPositions(payload));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("the trading server stream: failed to parse positions event", err);
      }
    });

    source.addEventListener("orders", (e: MessageEvent) => {
      try {
        const payload: TradingOrdersResponse = JSON.parse(e.data);
        dispatch(applyTradingOrders(payload));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("the trading server stream: failed to parse orders event", err);
      }
    });

    // EventSource fires `error` for both transient drops (it will
    // auto-reconnect after ~3s) and terminal failures. Flag the
    // chip as offline; on next `open` it flips back to live.
    source.addEventListener("error", () => {
      dispatch(setTradingStreamConnected(false));
    });

    return () => {
      source.close();
      dispatch(setTradingStreamConnected(false));
    };
  }, [dispatch, isOwner, meStatus]);
}
